import { useT } from '@/core/I18nProvider';

import { ResultCard } from './ResultCard';
import { useChatSim } from './useChatSim';

import type { ReactElement } from 'react';

/**
 * The live conversation on step 3 of the how-it-works stage.
 *
 * Every answer here is a CARD, not a paragraph — a receipt, a chart, a numbered
 * plan awaiting approval, a refusal naming who can act instead. That is the
 * claim the step makes ("it answers with real interface, not text"), so the
 * surface has to demonstrate it rather than assert it.
 *
 * Like the story stage, this is a mock: `inert`, out of the accessibility tree,
 * with a prose alternative supplied by the band. Seven fabricated support
 * transcripts read aloud would be noise, and the composer looks focusable while
 * being a `<span>`.
 */
export function ChatSurface({ active }: { readonly active: boolean }): ReactElement {
  const { t } = useT();
  const placeholder = t('home.how.chat.placeholder');
  const sim = useChatSim(active, placeholder, t);

  return (
    <div
      aria-hidden="true"
      {...{ inert: '' }}
      // The audit hook for `scripts/a11y/inert-contrast.mjs`. axe cannot see
      // inside an `inert` subtree, so this mock's colours are measured by that
      // script instead — and it fails loudly if this attribute goes missing,
      // rather than reporting the surface clean.
      data-how-chat
      // The MOTION hook, and it is a different concern from `data-how-chat`
      // above — that one is the contrast-audit hook. `bedrock.css`'s global
      // `prefers-reduced-motion: reduce` block selects `[data-bd-anim]` and its
      // descendants, and without it ten elements in this subtree went on
      // animating for a visitor who had asked their OS for less motion:
      // `sxUp` on nine bubbles and cards, and `sxCaret` — a blinking cursor —
      // on the composer.
      //
      // The JS side was already correct: `useChatSim` checks `matchMedia` and
      // never starts its loop. That is exactly what made this invisible — the
      // transcript sits still, so the band LOOKS settled, while its seed frame's
      // CSS animations keep running. A driver being gated is not the same as
      // the surface being still, and only the second one is what a visitor
      // asked for.
      data-bd-anim
      className="relative overflow-hidden rounded-2xl border border-[color:var(--bd-line-3)]"
      style={{
        background: 'linear-gradient(180deg,#fbfbfc,#fff)',
        boxShadow: '0 1px 2px rgba(12,13,16,.04), 0 10px 24px -14px rgba(12,13,16,.24)',
      }}
    >
      <div className="flex items-center gap-2.5 border-b border-[color:var(--bd-line)] bg-white px-3.5 py-2.5">
        <span
          aria-hidden="true"
          className="size-5 flex-none rounded-[7px]"
          style={{ background: 'var(--bd-gradient-135)' }}
        />
        <span className="text-xs font-semibold tracking-[-0.02em] text-[color:var(--bd-ink)]">
          {t('home.how.chat.title')}
        </span>
        <span className="ms-auto inline-flex items-center gap-1.5 font-mono rtl:font-sans text-[9.5px] uppercase tracking-[0.1em] text-[#0f766e] rtl:normal-case rtl:tracking-normal">
          <span
            aria-hidden="true"
            data-bd-anim
            className="size-1.5 rounded-full bg-[#34d399]"
            style={{ animation: 'bdPulse 2.2s ease-in-out infinite' }}
          />
          {t('home.how.chat.live')}
        </span>
      </div>

      <div
        className="relative h-[326px] overflow-hidden"
        style={{
          maskImage: 'linear-gradient(180deg,transparent,#000 9%,#000 100%)',
          WebkitMaskImage: 'linear-gradient(180deg,transparent,#000 9%,#000 100%)',
        }}
      >
        <div ref={sim.viewRef} className="h-full overflow-hidden px-3.5 pt-3.5">
          {/* Bottom-anchored: the transcript rides up as turns land, matching
              the real widget's `margin-block-start: auto` feed. */}
          <div className="flex min-h-full flex-col justify-end gap-2.5 pb-3.5">
            {sim.turns.map((turn) => (
              <div
                key={turn.key}
                className={[
                  'flex flex-col gap-1.5',
                  turn.side === 'agent' ? 'items-start' : 'items-end',
                ].join(' ')}
                style={{ animation: 'sxUp .4s cubic-bezier(.16,1,.3,1) backwards' }}
              >
                <div
                  className={[
                    'px-3 py-2 text-[12.5px] leading-snug',
                    turn.side === 'agent'
                      ? 'max-w-[94%] rounded-[13px_13px_13px_4px] border border-[#ececf0] bg-white text-[#2f2f38] rtl:rounded-[13px_13px_4px_13px]'
                      : 'max-w-[86%] rounded-[13px_13px_4px_13px] bg-[color:var(--bd-blue)] text-white rtl:rounded-[13px_13px_13px_4px]',
                  ].join(' ')}
                  style={{
                    boxShadow:
                      turn.side === 'agent'
                        ? '0 12px 24px -22px rgba(12,13,16,.7)'
                        : '0 10px 20px -16px rgba(43,98,255,.95)',
                  }}
                >
                  {t(turn.textKey)}
                </div>
                {turn.exchange ? <ResultCard exchange={turn.exchange} /> : null}
              </div>
            ))}

            {sim.workKey ? (
              <div
                className="flex items-center gap-2 self-start rounded-[13px_13px_13px_4px] border border-[#ececf0] bg-white px-3 py-2 rtl:rounded-[13px_13px_4px_13px]"
                style={{ animation: 'sxUp .3s ease backwards' }}
              >
                <span className="flex items-center gap-1">
                  {[0, 0.16, 0.32].map((delay) => (
                    <span
                      key={delay}
                      className="size-[5px] rounded-full bg-[color:var(--bd-blue)]"
                      style={{ animation: `bdPulse 1.1s ease-in-out ${delay}s infinite` }}
                    />
                  ))}
                </span>
                <span className="font-mono rtl:font-sans text-[9.5px] uppercase tracking-[0.09em] text-[color:var(--bd-faint)] rtl:normal-case rtl:tracking-normal">
                  {t(sim.workKey)}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2.5 border-t border-[color:var(--bd-line)] bg-white px-3 py-2.5">
        <div className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden rounded-[9px] border border-[color:var(--bd-line)] bg-[color:var(--bd-sunk)] px-3 py-2 text-xs">
          {/* Written to directly by the sim — it renders no React children so
              a re-render can never clobber the half-typed string. */}
          <span
            ref={sim.composerRef}
            className="truncate text-[color:var(--bd-faint)]"
            style={{ whiteSpace: 'nowrap' }}
          >
            {placeholder}
          </span>
          <span
            className="h-3 w-[1.5px] flex-none bg-[color:var(--bd-blue)]"
            style={{ animation: 'sxCaret 1.1s step-end infinite' }}
          />
        </div>
        <span className="grid size-8 flex-none place-items-center rounded-[9px] bg-[color:var(--bd-ink)] text-[13px] text-white rtl:-scale-x-100">
          ↑
        </span>
      </div>
    </div>
  );
}
